import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { readPage, isOk } from '../../../utils/apiResult';

// Task 34 — Faz A'nın döngü-bazlı /v1/nine-box/grid + override/manual-performance akışının
// yerini süreç-bazlı akış alıyor: IK bir NineBoxProcess başlatır (auto|form), matris seçili
// sürecin placement'larını gösterir. Eski uç (/v1/nine-box/grid) BE'de TalentDashboardPage
// için hâlâ duruyor (TODO-Task38) ama bu sayfa artık onu ÇAĞIRMIYOR.

// Süreç listesi (self-servis uç — GetMyPlacement ile aynı desen, modül kapısı YOK BE'de;
// MyNineBoxPage de "en son completed süreç" keşfi için bunu kullanır).
export const useNineBoxProcesses = () => {
  const [processes, setProcesses] = useState([]);
  const [fetching, setFetching] = useState(true);
  // TD-23 — "yüklenemedi" ≠ "hiç süreç yok". Sinyal res.data.status (bkz. utils/apiResult.js):
  // interceptor GET'lerde reject etmediği için .catch() dalı hiç çalışmıyordu.
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/nine-box/processes')
      .then((res) => {
        if (cancelled) return;
        const { ok, items } = readPage(res);
        setLoadFailed(!ok);
        setProcesses(items);
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const startProcess = useCallback(async (payload) => {
    try {
      await api.post('/v1/nine-box/processes', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // TD-13 — `force`: doldurulmamış puanlamalar varken turu KISMİ kapatır (BE eksik
  // satırları "skipped" damgalar). Bayrak gövdede taşınır; gövdesiz eski çağrı BE'de
  // force=false ile aynı davranışı alır.
  const completeProcess = useCallback(async (processId, force = false) => {
    try {
      await api.post(`/v1/nine-box/processes/${processId}/complete`, { force });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return {
    processes, fetching, loadFailed, refresh, startProcess, completeProcess,
  };
};

// Seçili sürecin grid'i (GET v1/nine-box/processes/{id}/grid — YENİ cellKey token'ları).
export const useNineBoxProcessGrid = (processId, filters) => {
  const [fetching, setFetching] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  // TD-12 — BE'nin ayrımı: çağıranın organizasyon şemasında aktif pozisyonu yoksa ekip
  // yürüyüşü hiç başlayamaz. Bu, "ekibimde kimse değerlendirilmemiş" ile aynı şey DEĞİL.
  const [scopeUnresolved, setScopeUnresolved] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!processId) {
      setGridData([]);
      setLoadFailed(false);
      setScopeUnresolved(false);
      return undefined;
    }
    let cancelled = false;
    setFetching(true);
    const params = {};
    if (filters?.departmentId) params['departmentId[eq]'] = filters.departmentId;
    api.get(`/v1/nine-box/processes/${processId}/grid?${new URLSearchParams(params)}`)
      .then((res) => {
        if (cancelled) return;
        const ok = isOk(res);
        const result = res.data?.data;
        setLoadFailed(!ok);
        setScopeUnresolved(ok && result?.scopeUnresolved === true);
        setGridData(ok ? (result?.cells || []) : []);
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [processId, filters, refreshTrigger]);

  return { fetching, gridData, loadFailed, scopeUnresolved, refresh };
};

// Task 34 — ManagerRatingPage: yöneticinin bekleyen ("requested") 9-Box puanlama
// talepleri (GET v1/nine-box/ratings/my-pending) + puan+gerekçe gönderimi (POST
// v1/nine-box/ratings/{id}/submit — Faz A NineBoxRules.ValidateManualEntry anahtarları
// yeniden kullanılır, bkz. INineBoxService.SubmitRatingAsync yorumu).
export const useMyPendingRatingsApi = () => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/nine-box/ratings/my-pending')
      .then((res) => {
        if (cancelled) return;
        const { ok, items } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const submitRating = useCallback(async (ratingId, score, reason) => {
    try {
      await api.post(`/v1/nine-box/ratings/${ratingId}/submit`, { score, reason });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return {
    data, fetching, loadFailed, refresh, submitRating,
  };
};

// Task 34 — MyNineBoxPage (self-view, /talent/my-ninebox): "en son completed süreç"
// GetProcesses'ten keşfedilir (BE'de ayrı bir "en son" ucu yok), ardından o sürecin
// GetMyPlacementAsync'i çağrılır. İkisi de skipErrorToast: module.notSubscribed /
// talent.ninebox.selfViewDisabled 409'ları jenerik toast YERİNE sayfanın kendi bilgi
// boş-durumuna düşer. NOT: services/api.js interceptor'ı başarısız GET'lerde HER ZAMAN
// mesajsız EMPTY_RESPONSE ile çözer (skipErrorToast'tan bağımsız) — orijinal responseError
// anahtarı (hangi 409 olduğu) FE'ye hiç ulaşmaz. Bu yüzden MyPeriodResultsPage/config'teki
// `notReady` deseniyle BİREBİR aynı basitleştirme: tek bir boolean ("gösterilecek bir şey
// yok"), üç olası neden (abonelik yok / self-view kapalı / henüz tamamlanmış süreç yok)
// kullanıcı için aynı sonuca (boş durum) düşüyor, ayrımına gerek yok.
export const useMyNineBoxApi = () => {
  const [placement, setPlacement] = useState(null);
  const [processName, setProcessName] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    setNotAvailable(false);
    setPlacement(null);
    setProcessName(null);

    api.get('/v1/nine-box/processes', { skipErrorToast: true })
      .then(async (res) => {
        if (cancelled) return;
        const body = res.data;
        if (!body?.status) {
          setNotAvailable(true);
          setFetching(false);
          return;
        }
        const r = body.data;
        const list = Array.isArray(r) ? r : (r?.data || []);
        const completed = list.filter((p) => p.status === 'completed');
        if (completed.length === 0) {
          // Abonelik/flag sorunu yok — yalnız henüz tamamlanmış bir süreç yok.
          setFetching(false);
          return;
        }
        // GetProcessesAsync en yeni önce döner — ilk completed = en son tamamlanan tur.
        const latest = completed[0];
        try {
          const res2 = await api.get(`/v1/nine-box/processes/${latest.id}/my`, { skipErrorToast: true });
          if (cancelled) return;
          const body2 = res2.data;
          if (!body2?.status) {
            setNotAvailable(true);
          } else {
            setProcessName(latest.name);
            setPlacement(body2.data || null);
          }
        } catch {
          if (!cancelled) setNotAvailable(true);
        } finally {
          if (!cancelled) setFetching(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotAvailable(true);
          setFetching(false);
        }
      });

    return () => { cancelled = true; };
  }, []);

  return {
    placement, processName, notAvailable, fetching,
  };
};

export default useNineBoxProcesses;
